import React, { useEffect, useMemo, useRef } from "react"
import { GlobalSidebar, SidebarList, AntdButton, FormInputAntd, FormCheckbox } from "components"
import { yupResolver } from "@hookform/resolvers/yup"
import { SDEquipmentTypeInterface } from "@type/staticData.types"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { SDEquipmentTypeInitialValues, SDEquipmentTypeSchema } from "Schema/StaticData/SDEquipmentType"
import {
	getSDEquipmentType,
	isSDEquipmentTypeLoading,
	isSDEquipmentTypeSaving,
	saveSDEquipmentType,
	selectSDEquipmentTypeById,
	selectSDEquipmentTypeList,
} from "store/StaticData/SDEquipmentTypes.slice"
import { useAppDispatch, useAppSelector } from "hooks"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface EquipmentTypeFormHandler extends SDEquipmentTypeInterface {}

const EquipmentTypes = () => {
	const dispatch = useAppDispatch()
	const saving = useAppSelector(isSDEquipmentTypeSaving())
	const loading = useAppSelector(isSDEquipmentTypeLoading())
	const SDEquipmentTypesList = useAppSelector(selectSDEquipmentTypeList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDEquipmentType = "" } = params
	const SDEquipmentType = useAppSelector(selectSDEquipmentTypeById(selectedSDEquipmentType))
	const isNewBeingCreated = useRef(false)
	const methods = useForm<EquipmentTypeFormHandler>({ resolver: yupResolver(SDEquipmentTypeSchema) })

	const {
		handleSubmit,
		reset,
		formState: { isSubmitted, isDirty, isSubmitting },
	} = methods

	const sidebarSDEquipmentType = useMemo(() => {
		return (SDEquipmentTypesList ?? [])?.map(SDEquipmentType => ({
			_id: SDEquipmentType._id,
			name: SDEquipmentType.description,
		}))
	}, [saving, SDEquipmentTypesList?.length, isSubmitted])

	const onSubmit: SubmitHandler<EquipmentTypeFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			saveSDEquipmentType(data, id => {
				setSearchParams({ selected: id })
			}),
		)
	}

	useEffect(() => {
		dispatch(getSDEquipmentType())
	}, [])

	useEffect(() => {
		if (!selectedSDEquipmentType && !!SDEquipmentTypesList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDEquipmentTypesList?.[0]?._id })
		}
	}, [params, SDEquipmentTypesList])

	useEffect(() => {
		if (selectedSDEquipmentType && SDEquipmentType) {
			reset({ ...SDEquipmentTypeInitialValues, ...SDEquipmentType })
		} else if (!selectedSDEquipmentType && !isNewBeingCreated.current) {
			reset({ ...SDEquipmentTypeInitialValues })
		}
	}, [selectedSDEquipmentType, SDEquipmentType])

	const handleSelected = (SDEquipmentTypeId: string): void => {
		setSearchParams({ selected: SDEquipmentTypeId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"Equipment Type"}
					loading={loading}
					selected={selectedSDEquipmentType}
					setSelected={handleSelected}
					list={sidebarSDEquipmentType.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex w-1/4 flex-col gap-2">
						<FormInputAntd type="text" label="Description" name="description" />
						<FormCheckbox label="Allocate to Employee" name="allocateToEmployee" />
						<FormCheckbox label="Allocate to Site" name="allocateToSite" />
						<div className="flex gap-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDEquipmentTypeInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
								{selectedSDEquipmentType ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default EquipmentTypes
